import React, { useState } from 'react';
import { Plan } from '../../types/Plan';
import { textoSobre } from '../../utils/color';
import { Badge } from '../ui';
import { UserIcon } from '../icons';

export interface PlanIdentidadProps {
  plan: Plan;
  /** Versión compacta para el encabezado sticky de la tabla comparativa. */
  compact?: boolean;
}

/**
 * Cabecera tipo "credencial impresa": rectángulo con el color de marca del
 * plan, ícono oficial (si carga) y nombre. Debajo, chips neutros con la
 * cláusula del TyC, el público restringido y si el plan no otorga descuentos.
 *
 * El color de fondo viene de Salesforce y varía mucho en luminosidad, así que
 * el color de texto se calcula con `textoSobre` para no romper el contraste.
 * El ícono es un SVG remoto que puede fallar: si falla, se oculta sin dejar
 * un hueco roto y el nombre del plan sigue siendo la referencia principal.
 */
const PlanIdentidad: React.FC<PlanIdentidadProps> = ({ plan, compact = false }) => {
  const [iconoFalla, setIconoFalla] = useState(false);
  const colorTexto = textoSobre(plan.color);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className={`flex items-center rounded-lg ${compact ? 'gap-2 px-2.5 py-2' : 'gap-3 px-4 py-3'}`}
        style={{ backgroundColor: plan.color, color: colorTexto }}
      >
        {plan.iconoUrl && !iconoFalla && (
          // El anillo va en el recuadro blanco y no en el <img>: el ícono es
          // transparente, así que un outline sobre él dibujaría una caja
          // flotando alrededor del glifo en vez del borde de la superficie.
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-md bg-white/90 outline outline-1 -outline-offset-1 outline-black/10 ${
              compact ? 'h-6 w-6 p-1' : 'h-9 w-9 p-1.5'
            }`}
          >
            <img
              src={plan.iconoUrl}
              alt=""
              className="h-full w-full object-contain"
              onError={() => setIconoFalla(true)}
            />
          </span>
        )}
        <span
          className={`truncate font-poppins font-bold leading-tight ${compact ? 'text-sm' : 'text-lg'}`}
        >
          {plan.nombre}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className={compact ? 'px-1.5 text-[10px]' : ''}>TyC {plan.clausula}</Badge>
        {plan.publico && (
          <Badge className={`max-w-full gap-1 ${compact ? 'px-1.5 text-[10px]' : ''}`}>
            <UserIcon className="h-3 w-3 shrink-0" strokeWidth={2} />
            <span className="truncate">{plan.publico}</span>
          </Badge>
        )}
        {!plan.tieneDescuentos && (
          <Badge className={`max-w-full ${compact ? 'px-1.5 text-[10px]' : ''}`}>
            Sin descuentos médicos
          </Badge>
        )}
      </div>
    </div>
  );
};

export default PlanIdentidad;
